import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { AttributeValue } from "@sinch/components/application/Attribute/AttributeValue";
import { StaffHeader } from "@sinch/components/application/Staff/StaffHeader/StaffHeader";
import React, { FC } from "react";
import { StaffTooltipQuery } from "./queries.types";
import { MdsIcon } from "@sinch/components";
import { useTranslation } from "react-i18next";
import { MdsMailXs, MdsMobileChatXs, MdsOpenInNewXs } from "@sinch/icons";

type StaffWorker = NonNullable<StaffTooltipQuery["worker"]>;

interface StaffTooltipContentProps {
  /** Worker to render, or nullish while the lazy query is still loading. */
  worker: StaffWorker | null | undefined;
}

/**
 * Body of the staff profile card: the header plus the worker's important personal
 * and profile attributes. Renders a spinner until the worker data arrives.
 */
export const StaffTooltipContent: FC<StaffTooltipContentProps> = ({ worker }) => {
  const { t } = useTranslation();
  if (!worker) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 80, minWidth: 240, p: 5 }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const attributes = [...(worker.personalAttributes ?? []), ...(worker.profileAttributes ?? [])];

  return (
    <>
      <StaffHeader avatarSize="lg" staff={worker} />

      <Stack direction="column">
        {attributes.map(({ id, value, attribute, files, expirationDate }) => (
          <AttributeValue
            key={id}
            expirationDate={expirationDate}
            files={files}
            label={attribute.name}
            options={attribute.options}
            type={attribute.dataType}
            value={value}
          />
        ))}
      </Stack>
      <Stack direction="row" gap={1} sx={{ pt: 1 }}>
        <Button
          href={`/admin/email_newsletters/send?user_id[]=${worker.id}`}
          size="small"
          startIcon={<MdsIcon fontSize="xs" icon={MdsMailXs} />}
          variant="outlined"
        >
          {t("Messages.channelEmail")}
        </Button>
        <Button
          href={`/admin/sms/send?user_id[]=${worker.id}`}
          size="small"
          startIcon={<MdsIcon fontSize="xs" icon={MdsMobileChatXs} />}
          variant="outlined"
        >
          {t("Messages.channelSms")}
        </Button>
        <Button
          endIcon={<MdsIcon fontSize="xs" icon={MdsOpenInNewXs} />}
          href={`/admin/workers/view/${worker.id}`}
          size="small"
          sx={{ ml: "auto" }}
          variant="outlined"
        >
          {t("Action.view")}
        </Button>
      </Stack>
    </>
  );
};
